import {MouseEventHandler, useCallback} from 'react';
import {styled} from 'styled-components';
import {Redirect, useLocation, useParams} from 'wouter';

import {ProjectSex} from '@shared/api/app_api';

import {NameView} from '@src/components/name_view';
import {getScoreInfo} from '@src/components/score';
import {useDb} from '@src/lib/db_store';
import {FullScreenMessage} from '@src/lib/full_screen_message';
import {setProjectScore, useProjects} from '@src/lib/projects_stores';

export const ProjectScorePage: React.FC = () => {
  const [_, setLocation] = useLocation();
  const projects = useProjects();
  const {id = '', key} = useParams();

  const project = projects.find(p => p.id === id);
  const db = useDb((project?.settings.sexes ?? []) as ProjectSex[]);

  const handleScoreClick = useCallback<MouseEventHandler>(
    evt => {
      const scoreStr = evt.currentTarget.getAttribute('data-score');
      if (scoreStr === null || key === undefined) {
        return;
      }
      const score = parseFloat(scoreStr);
      if (isNaN(score)) {
        return;
      }
      const nextName = db?.find(n => project?.scores[n.key] === undefined && n.key !== key);
      setProjectScore(id, key, score);
      if (nextName) {
        setLocation(`/project/${id}/score/${nextName.key}`);
      }
    },
    [db, id, key, project?.scores, setLocation]
  );

  if (!project) {
    console.log('ProjectScorePage: project not found');
    return <Redirect to={'/'} />;
  }

  if (!db) {
    return <FullScreenMessage>Base de données en cours de téléchargement</FullScreenMessage>;
  }

  if (key === undefined) {
    const index = db.findIndex(({key}) => project.scores[key] === undefined);
    const to = index === -1 ? `/project/${id}` : `/project/${id}/score/${db[index]?.key}`;
    console.log('ProjectScorePage: redirect to', to);
    return <Redirect to={to} />;
  }

  const index = db.findIndex(p => p.key === key);
  const dbName = db[index];

  if (!dbName) {
    return <Redirect to={`/project/${id}`} />;
  }

  const currentScore = project.scores[key];

  return (
    <NameView
      db={db}
      nameKey={key}
      backPath={`/project/${id}`}
      navPathPrefix={`/project/${id}/score/`}
      bottom={
        <ButtonsLines>
          {[
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            [15, 85],
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            [35, 60],
          ].map((line, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <ButtonLine key={i}>
              {line.map(score => {
                const {label, color} = getScoreInfo(score);
                return (
                  <ScoreButton
                    key={score}
                    data-score={score}
                    $color={color}
                    $selected={currentScore === score}
                    onClick={handleScoreClick}
                  >
                    {label}
                  </ScoreButton>
                );
              })}
            </ButtonLine>
          ))}
        </ButtonsLines>
      }
    />
  );
};
ProjectScorePage.displayName = 'ProjectScorePage';

const ButtonsLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
`;

const ButtonLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ScoreButton = styled.button<{$color: string; $selected: boolean}>`
  width: 0;
  flex-grow: 1;
  flex-basis: 1px;
  font-size: 1.5rem;
  padding: 1rem;
  background-color: ${p => p.$color};
  color: white;
  border: solid 0.25rem ${p => (p.$selected ? '#cbcbcb' : 'transparent')};
  cursor: pointer;
  border-radius: 0.5rem;
  white-space: nowrap;
`;
